import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { authService } from "@/services/authService";

const AuthContext = createContext(null);

const TIER_RANK = { demo: 0, starter: 1, professional: 2 };

export const AuthProvider = ({ children }) => {
    const [account, setAccount] = useState(null);
    const [status, setStatus] = useState("loading"); // loading | authenticated | anonymous

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const session = await authService.getSession();
            if (cancelled) return;
            if (session?.account) {
                setAccount(session.account);
                setStatus("authenticated");
            } else {
                setStatus("anonymous");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const signIn = useCallback(async (creds) => {
        const { account: a } = await authService.signIn(creds);
        setAccount(a);
        setStatus("authenticated");
        return a;
    }, []);

    const signUp = useCallback(async (data) => {
        const { account: a } = await authService.signUp(data);
        setAccount(a);
        setStatus("authenticated");
        return a;
    }, []);

    const signOut = useCallback(async () => {
        await authService.signOut();
        setAccount(null);
        setStatus("anonymous");
    }, []);

    const refresh = useCallback(async () => {
        const session = await authService.getSession();
        if (session?.account) {
            setAccount(session.account);
            setStatus("authenticated");
        } else {
            setAccount(null);
            setStatus("anonymous");
        }
    }, []);

    const value = useMemo(
        () => ({ account, status, signIn, signUp, signOut, refresh }),
        [account, status, signIn, signUp, signOut, refresh],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
};

export const useTier = () => {
    const { account } = useAuth();
    const tier = account?.subscription?.tier ?? "demo";
    const rank = TIER_RANK[tier] ?? 0;
    const meets = (min) => rank >= (TIER_RANK[min] ?? 0);
    return { tier, rank, meets };
};
